// ============================================
// Nurse Tuning Panel — TARS-Style Trait Calibration
// Adjustable personality sliders for the AI Nurse.
// NO framer-motion — matches FinancialDashboard pattern
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { Sliders, Save, RotateCcw, Trash2, Star, Copy, Play, Loader2, CheckCircle2, XCircle, User } from 'lucide-react';
import type { NurseTraitProfile, BehaviorModel, Message, CheckResult } from '../../../types/patientJourney';
import { DEFAULT_TRAIT_PROFILE } from '../../../types/patientJourney';
import {
  saveTraitProfile,
  getTraitProfiles,
  deleteTraitProfile,
  type SavedTraitProfile,
} from '../../../services/firestore/pjeService';
import {
  runSingleTwinV3,
  type MessageCallback,
  type CheckScoredCallback,
} from '../../../services/patientJourney/twinService';
import { cachePJEConfig, getPJEConfig } from '../../../services/patientJourney/pjeConfig';
import { generateV3Checks, detectConversationModes, generateContextChecks } from '../../../services/patientJourney/contextEngine';
import { PERSONA_MAP, buildDefaultTouchpoint } from './TwinRunner';

// --- Persona Options (mirrors TwinRunner) ---

const PERSONA_PICKER: { value: string; label: string }[] = [
  { value: 'clean_gate1', label: 'Sarah Mitchell — Clean Gate 1' },
  { value: 'drug_interaction', label: 'Maria Rodriguez — Drug Interaction' },
  { value: 'skeptic', label: 'Dr. Jennifer Walsh — The Skeptic' },
  { value: 'mens_low_t', label: 'James Cooper — Men\'s Low-T' },
  { value: 'mens_polypharm', label: 'Robert Chen — Polypharmacy' },
  { value: 'thyroid_hypo', label: 'Amanda Foster — Hypothyroid' },
  { value: 'metabolic_ir', label: 'David Park — Insulin Resistance' },
  { value: 'fertility_pcos', label: 'Rachel Kim — PCOS / Fertility' },
];

// --- Trait Definitions ---

interface TraitDef {
  key: keyof NurseTraitProfile;
  label: string;
  description: string;
  lowLabel: string;
  highLabel: string;
  color: string; // tailwind color class for the slider fill
}

const TRAIT_DEFS: TraitDef[] = [
  {
    key: 'empathy',
    label: 'Empathy',
    description: 'How warm vs clinical the nurse tone is. High = Guide-forward, Low = Analyst-forward.',
    lowLabel: 'Clinical',
    highLabel: 'Warm',
    color: 'bg-pink-500',
  },
  {
    key: 'dataDensity',
    label: 'Data Density',
    description: 'Biomarker value references per response. High = dense data tables, Low = summary only.',
    lowLabel: 'Summary',
    highLabel: 'Dense',
    color: 'bg-cyan-500',
  },
  {
    key: 'explanationDepth',
    label: 'Explanation Depth',
    description: 'How deeply the nurse explains lab ranges and clinical reasoning.',
    lowLabel: 'Brief',
    highLabel: 'Thorough',
    color: 'bg-violet-500',
  },
  {
    key: 'productTiming',
    label: 'Product Timing',
    description: 'How early/late the nurse introduces protocol and supplement recommendations.',
    lowLabel: 'Early',
    highLabel: 'Late',
    color: 'bg-amber-500',
  },
  {
    key: 'verificationFreq',
    label: 'Verification',
    description: 'How often the nurse asks follow-up questions to check patient understanding.',
    lowLabel: 'Rare',
    highLabel: 'Frequent',
    color: 'bg-green-500',
  },
  {
    key: 'urgencyCalibration',
    label: 'Urgency',
    description: 'How alarmed the nurse sounds for different severity levels. High = faster escalation.',
    lowLabel: 'Calm',
    highLabel: 'Urgent',
    color: 'bg-red-500',
  },
];

// --- Built-In Presets ---

const BUILT_IN_PRESETS: { name: string; description: string; profile: NurseTraitProfile }[] = [
  {
    name: 'Balanced',
    description: 'Default HemoLogix — data-forward with empathy anchoring',
    profile: { ...DEFAULT_TRAIT_PROFILE },
  },
  {
    name: 'Education-Forward',
    description: 'Maximum explanation depth and verification — great for new patients',
    profile: { empathy: 65, dataDensity: 70, explanationDepth: 95, productTiming: 80, verificationFreq: 85, urgencyCalibration: 25 },
  },
  {
    name: 'Clinical Precision',
    description: 'Analyst-heavy — dense data, minimal hand-holding, fast protocol',
    profile: { empathy: 25, dataDensity: 95, explanationDepth: 40, productTiming: 20, verificationFreq: 30, urgencyCalibration: 60 },
  },
  {
    name: 'Empathy First',
    description: 'Guide-heavy — warm, protective, explains everything in felt terms',
    profile: { empathy: 95, dataDensity: 35, explanationDepth: 70, productTiming: 75, verificationFreq: 70, urgencyCalibration: 20 },
  },
  {
    name: 'Sales Optimized',
    description: 'Early product timing, moderate empathy, fast-paced',
    profile: { empathy: 55, dataDensity: 50, explanationDepth: 40, productTiming: 15, verificationFreq: 40, urgencyCalibration: 50 },
  },
];

// --- Helpers ---

function generateId(): string {
  return `tp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 8)}`;
}

// --- Component ---

export function NurseTuning() {
  const [traits, setTraits] = useState<NurseTraitProfile>({ ...DEFAULT_TRAIT_PROFILE });
  const [savedProfiles, setSavedProfiles] = useState<SavedTraitProfile[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [profileName, setProfileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // --- Run state ---
  const [selectedPersona, setSelectedPersona] = useState('clean_gate1');
  const [isRunning, setIsRunning] = useState(false);
  const [runMessages, setRunMessages] = useState<Message[]>([]);
  const [runCheckResults, setRunCheckResults] = useState<CheckResult[]>([]);
  const [runPassRate, setRunPassRate] = useState<number | null>(null);
  const [runDuration, setRunDuration] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Load saved profiles from Firestore
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const profiles = await getTraitProfiles();
        if (!cancelled) setSavedProfiles(profiles);
      } catch {
        // Firestore may not have the collection yet — OK
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const handleTraitChange = useCallback((key: keyof NurseTraitProfile, value: number) => {
    setTraits((prev) => ({ ...prev, [key]: value }));
    setActiveProfileId(null); // mark as custom/unsaved
  }, []);

  const handleLoadPreset = useCallback((profile: NurseTraitProfile, id?: string) => {
    setTraits({ ...profile });
    setActiveProfileId(id ?? null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!profileName.trim()) return;
    setSaving(true);
    try {
      const id = generateId();
      const now = new Date().toISOString();
      const profile: SavedTraitProfile = {
        profileId: id,
        name: profileName.trim(),
        traitProfile: { ...traits },
        isDefault: false,
        createdAt: now,
        updatedAt: now,
      };
      await saveTraitProfile(profile);
      setSavedProfiles((prev) => [profile, ...prev]);
      setActiveProfileId(id);
      setProfileName('');
      setShowSaveForm(false);
    } catch {
      // Save failed — user sees no change
    } finally {
      setSaving(false);
    }
  }, [profileName, traits]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteTraitProfile(id);
      setSavedProfiles((prev) => prev.filter((p) => p.profileId !== id));
      if (activeProfileId === id) setActiveProfileId(null);
    } catch {
      // Delete failed
    }
    setDeleteConfirm(null);
  }, [activeProfileId]);

  const handleReset = useCallback(() => {
    setTraits({ ...DEFAULT_TRAIT_PROFILE });
    setActiveProfileId(null);
  }, []);

  // --- Run with current traits ---
  const handleRun = useCallback(async () => {
    const persona = PERSONA_MAP[selectedPersona];
    if (!persona) return;

    setIsRunning(true);
    setRunMessages([]);
    setRunCheckResults([]);
    setRunPassRate(null);
    setRunDuration(null);
    setRunError(null);

    try {
      // Inject current traits into config cache so the engine picks them up
      const cfg = { ...getPJEConfig(), traitProfile: traits };
      cachePJEConfig('hemologix', cfg);

      // Build touchpoint with v3 checks
      const mergedChecks = [...persona.validationChecklist];
      const modes = detectConversationModes(persona.contextModifiers);
      const ctxChecks = generateContextChecks(modes);
      const existingIds = new Set(mergedChecks.map(c => c.checkId));
      for (const c of ctxChecks) {
        if (!existingIds.has(c.checkId)) mergedChecks.push(c);
      }
      const v3Checks = generateV3Checks('blended', 'phase1_scripted', false);
      for (const c of v3Checks) {
        if (!existingIds.has(c.checkId)) mergedChecks.push(c);
      }

      const touchpoint = persona.journeyTouchpoints.length > 0
        ? { ...persona.journeyTouchpoints[0], validationChecklist: mergedChecks }
        : { ...buildDefaultTouchpoint(persona.personaId), validationChecklist: mergedChecks };

      const behavior: BehaviorModel = {
        communicationStyle: 'conversational',
        questionFrequency: 'moderate',
        attentionSpan: 'medium',
        decisionSpeed: 'moderate',
        skepticismLevel: 40,
        complianceTendency: 60,
        priceResistance: 30,
        emotionalExpressiveness: 50,
        objectionPatterns: [],
        dataSource: 'manual',
        dataVersion: 1,
      };

      const onMessage: MessageCallback = (msg) => {
        setRunMessages((prev) => [...prev, msg]);
      };

      const onCheckScored: CheckScoredCallback = (cr) => {
        setRunCheckResults((prev) => [...prev, cr]);
      };

      const result = await runSingleTwinV3(
        persona,
        behavior,
        touchpoint,
        20,
        undefined,
        onMessage,
        onCheckScored,
        cfg,
      );

      setRunPassRate(result.passRate);
      setRunDuration(result.duration);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Run failed');
    } finally {
      setIsRunning(false);
    }
  }, [selectedPersona, traits]);

  // Compute a summary string for the current profile
  const profileSummary = TRAIT_DEFS.map((t) => {
    const val = traits[t.key];
    const level = val >= 80 ? 'HIGH' : val >= 50 ? 'MED' : 'LOW';
    return `${t.label}: ${level}`;
  }).join(' · ');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Sliders size={24} className="text-gold" />
          <div>
            <h3 className="text-2xl font-display text-gold">Nurse Tuning</h3>
            <p className="text-base text-text-secondary mt-1">
              TARS-style personality calibration — adjust the AI nurse's behavioral traits
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors text-base"
          >
            <RotateCcw size={16} />
            Reset
          </button>
          <button
            onClick={() => setShowSaveForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gold/20 text-gold hover:bg-gold/30 transition-colors text-base"
          >
            <Save size={16} />
            Save Preset
          </button>
        </div>
      </div>

      {/* Current summary bar */}
      <div className="bg-bg-card rounded-xl border border-bg-tertiary px-4 py-3">
        <p className="text-sm text-text-muted font-mono">{profileSummary}</p>
        {activeProfileId && (
          <p className="text-sm text-gold mt-1">
            Active: {savedProfiles.find((p) => p.profileId === activeProfileId)?.name || 'Custom'}
          </p>
        )}
      </div>

      {/* Save form (conditional) */}
      {showSaveForm && (
        <div className="bg-bg-card rounded-2xl border border-gold/30 p-6">
          <h4 className="text-lg font-semibold text-text-primary mb-3">Save Current Settings as Preset</h4>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Preset name (e.g., 'Conservative Clinical')"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              className="flex-1 px-4 py-2 rounded-xl bg-bg-tertiary border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold"
            />
            <button
              onClick={handleSave}
              disabled={saving || !profileName.trim()}
              className="px-6 py-2 rounded-xl bg-gold text-bg-primary font-semibold text-base disabled:opacity-40 hover:bg-gold/90 transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setShowSaveForm(false)}
              className="px-4 py-2 rounded-xl bg-bg-tertiary text-text-secondary hover:text-text-primary transition-colors text-base"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sliders Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {TRAIT_DEFS.map((trait) => (
          <div
            key={trait.key}
            className="bg-bg-card rounded-2xl border border-bg-tertiary p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-lg font-semibold text-text-primary">{trait.label}</h4>
              <span className="text-2xl font-mono font-bold text-gold">{traits[trait.key]}</span>
            </div>
            <p className="text-sm text-text-secondary mb-4">{trait.description}</p>

            {/* Slider */}
            <div className="relative">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={traits[trait.key]}
                onChange={(e) => handleTraitChange(trait.key, Number(e.target.value))}
                className="w-full h-3 rounded-full appearance-none cursor-pointer bg-bg-tertiary
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:h-6
                  [&::-webkit-slider-thumb]:w-6
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-gold
                  [&::-webkit-slider-thumb]:border-2
                  [&::-webkit-slider-thumb]:border-bg-primary
                  [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:shadow-lg
                  [&::-moz-range-thumb]:h-6
                  [&::-moz-range-thumb]:w-6
                  [&::-moz-range-thumb]:rounded-full
                  [&::-moz-range-thumb]:bg-gold
                  [&::-moz-range-thumb]:border-2
                  [&::-moz-range-thumb]:border-bg-primary
                  [&::-moz-range-thumb]:cursor-pointer"
              />
              {/* Track fill using CSS gradient */}
              <div
                className={`absolute top-0 left-0 h-3 rounded-full pointer-events-none ${trait.color}`}
                style={{ width: `${traits[trait.key]}%`, opacity: 0.6 }}
              />
            </div>

            {/* Labels */}
            <div className="flex justify-between mt-2">
              <span className="text-xs text-text-muted">{trait.lowLabel}</span>
              <span className="text-xs text-text-muted">{trait.highLabel}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Presets Section */}
      <div>
        <h4 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Star size={18} className="text-gold" />
          Built-In Presets
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {BUILT_IN_PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => handleLoadPreset(preset.profile)}
              className="text-left bg-bg-card rounded-xl border border-bg-tertiary p-4 hover:border-gold/50 transition-colors"
            >
              <p className="text-base font-semibold text-text-primary">{preset.name}</p>
              <p className="text-sm text-text-secondary mt-1">{preset.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {TRAIT_DEFS.map((t) => {
                  const v = preset.profile[t.key];
                  return (
                    <span
                      key={t.key}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        v >= 80 ? 'bg-green-500/20 text-green-400'
                        : v >= 50 ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {t.label.substring(0, 3)} {v}
                    </span>
                  );
                })}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Saved Custom Presets */}
      {(savedProfiles.length > 0 || loading) && (
        <div>
          <h4 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Copy size={18} className="text-gold" />
            Saved Presets
          </h4>
          {loading ? (
            <p className="text-text-muted text-base">Loading saved presets...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedProfiles.map((sp) => (
                <div
                  key={sp.profileId}
                  className={`bg-bg-card rounded-xl border p-4 transition-colors ${
                    activeProfileId === sp.profileId
                      ? 'border-gold'
                      : 'border-bg-tertiary hover:border-gold/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <button
                      onClick={() => handleLoadPreset(sp.traitProfile, sp.profileId)}
                      className="text-left flex-1"
                    >
                      <p className="text-base font-semibold text-text-primary">{sp.name}</p>
                      {sp.description && (
                        <p className="text-sm text-text-secondary mt-1">{sp.description}</p>
                      )}
                    </button>
                    {deleteConfirm === sp.profileId ? (
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => handleDelete(sp.profileId)}
                          className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2 py-1 text-xs rounded bg-bg-tertiary text-text-muted hover:text-text-primary"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(sp.profileId)}
                        className="ml-2 p-1 text-text-muted hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {TRAIT_DEFS.map((t) => {
                      const v = sp.traitProfile[t.key];
                      return (
                        <span
                          key={t.key}
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            v >= 80 ? 'bg-green-500/20 text-green-400'
                            : v >= 50 ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {t.label.substring(0, 3)} {v}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Test Run Panel */}
      <div className="bg-bg-card rounded-2xl border border-bg-tertiary p-6">
        <h4 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Play size={18} className="text-gold" />
          Test With Current Traits
        </h4>

        {/* Patient Picker + Run Button */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1">
            <User size={16} className="text-text-muted flex-shrink-0" />
            <select
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
              disabled={isRunning}
              className="flex-1 px-4 py-2.5 rounded-xl bg-bg-tertiary border border-bg-tertiary text-text-primary text-base focus:outline-none focus:border-gold disabled:opacity-50"
            >
              {PERSONA_PICKER.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleRun}
            disabled={isRunning}
            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-base transition-colors ${
              isRunning
                ? 'bg-gold/30 text-gold/60 cursor-not-allowed'
                : 'bg-gold text-bg-primary hover:bg-gold/90'
            }`}
          >
            {isRunning ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play size={16} />
                Run Test
              </>
            )}
          </button>
        </div>

        {/* Error */}
        {runError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-sm text-red-400">{runError}</p>
          </div>
        )}

        {/* Results */}
        {runPassRate !== null && (
          <div className="mb-4">
            <div className="flex items-center gap-4 mb-3">
              <div className={`text-3xl font-mono font-bold ${
                runPassRate >= 80 ? 'text-green-400' : runPassRate >= 60 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {runPassRate}%
              </div>
              <div>
                <p className="text-base text-text-primary font-medium">Pass Rate</p>
                <p className="text-sm text-text-muted">
                  {runCheckResults.filter(c => c.pass).length}/{runCheckResults.length} checks
                  {runDuration ? ` · ${(runDuration / 1000).toFixed(0)}s` : ''}
                  {` · ${runMessages.length} messages`}
                </p>
              </div>
            </div>

            {/* Check results grid */}
            <div className="space-y-1.5">
              {runCheckResults.map((cr, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  {cr.pass ? (
                    <CheckCircle2 size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                  )}
                  <span className={cr.pass ? 'text-text-secondary' : 'text-red-300'}>
                    {cr.checkId}: {cr.notes ? cr.notes.substring(0, 120) : (cr.pass ? 'Passed' : 'Failed')}
                    {cr.notes && cr.notes.length > 120 ? '...' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live conversation preview (while running) */}
        {isRunning && runMessages.length > 0 && (
          <div className="border-t border-bg-tertiary pt-3 mt-3">
            <p className="text-xs text-text-muted font-mono mb-2">LIVE · {runMessages.length} messages</p>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {runMessages.slice(-4).map((msg, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded-lg ${
                  msg.role === 'ai_nurse' || msg.role === 'nurse'
                    ? 'bg-cyan-500/10 text-text-secondary'
                    : 'bg-violet-500/10 text-text-secondary'
                }`}>
                  <span className="font-mono text-xs text-text-muted">
                    {msg.role === 'ai_nurse' || msg.role === 'nurse' ? 'NURSE' : 'PATIENT'}:
                  </span>{' '}
                  {msg.content.substring(0, 200)}{msg.content.length > 200 ? '...' : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No run yet */}
        {runPassRate === null && !isRunning && (
          <p className="text-sm text-text-muted text-center py-4">
            Adjust traits above, pick a patient, and hit Run Test to see how the nurse performs.
          </p>
        )}
      </div>
    </div>
  );
}
